<template>
  <AdminLayout>
    <AdminPreloader :loading="loadingOrders" message="Loading orders..." />
    <div class="admin-orders">
      <h2 class="mb-4">Manage Orders</h2>

      <div class="filters-container mb-4">
        <div class="row g-3">
          <div class="col-md-6">
            <div class="search-box">
              <i class="fas fa-search"></i>
              <input type="text" v-model="searchQuery" class="form-control"
                placeholder="Search by order number or customer name...">
            </div>
          </div>
          <div class="col-md-3">
            <select v-model="statusFilter" class="form-select">
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      <div class="orders-table-container">
        <table class="modern-orders-table">
          <thead>
            <tr>
              <!-- <th>Order ID</th> -->
              <th>Order Number</th>
              <th>Customer</th>
              <th>Date</th>
              <th>Total</th>
              <th>Payment</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="order in paginatedOrders" :key="order.id" class="order-row">
              <!-- <td class="order-id">#{{ order.id }}</td> -->
              <td>{{ order.order_number || 'N/A' }}</td>
              <td>
                <div class="customer-info">
                  <!-- <div class="customer-avatar">
                    {{ order.customer ? order.customer.charAt(0).toUpperCase() : 'U' }}
                  </div> -->
                  <span class="customer-name">{{ order.customer || 'N/A' }}</span>
                </div>
              </td>
              <td class="order-date">{{ new Date(order.date).toLocaleDateString() }}</td>
              <td class="order-total">${{ order.total }}</td>
              <td>
                <span :class="['status-badge', getPaymentStatusClass(order.payment_status)]">
                  {{ order.payment_status || 'pending' }}
                </span>
              </td>
              <td>
                <select :value="order.status" @change="updateStatus(order.id, $event.target.value)"
                  class="form-select form-select-sm status-select" style="width: auto;">
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </td>
              <td>
                <router-link :to="'/admin/orders/' + order.id" class="action-btn">
                  <i class="fas fa-eye"></i>
                  View
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-container" v-if="totalPages > 1">
        <div class="pagination-info">
          Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to {{ Math.min(currentPage * itemsPerPage, totalOrders) }}
          of {{ totalOrders }} entries
        </div>
        <div class="pagination-controls">
          <button class="pagination-btn" :disabled="currentPage === 1" @click="currentPage--">
            <i class="fas fa-chevron-left"></i> Previous
          </button>
          <button v-for="page in visiblePages" :key="page" class="pagination-btn"
            :class="{ active: page === currentPage }" @click="currentPage = page">
            {{ page }}
          </button>
          <button class="pagination-btn" :disabled="currentPage === totalPages" @click="currentPage++">
            Next <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>

    </div>
  </AdminLayout>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import AdminLayout from '../components/AdminLayout.vue'
import AdminPreloader from '../components/AdminPreloader.vue'
import { useApiStore } from '../stores/api'

export default {
  name: 'AdminOrders',
  components: {
    AdminLayout,
    AdminPreloader
  },
  setup() {
    const orders = ref([])
    const loadingOrders = ref(false)
    const searchQuery = ref('')
    const statusFilter = ref('all')
    const apiStore = useApiStore()

    // Pagination state
    const currentPage = ref(1)
    const itemsPerPage = ref(10)

    const filteredOrders = computed(() => {
      let result = orders.value

      // Apply status filter
      if (statusFilter.value !== 'all') {
        result = result.filter(order => order.status === statusFilter.value)
      }

      // Apply search query
      if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase()
        result = result.filter(order =>
          (order.order_number && order.order_number.toLowerCase().includes(query)) ||
          (order.customer && order.customer.toLowerCase().includes(query)) ||
          order.id.toString().includes(query)
        )
      }

      return result
    })

    const totalOrders = computed(() => filteredOrders.value.length)
    const totalPages = computed(() => Math.ceil(totalOrders.value / itemsPerPage.value))

    const paginatedOrders = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage.value
      const end = start + itemsPerPage.value
      return filteredOrders.value.slice(start, end)
    })

    const visiblePages = computed(() => {
      const pages = []
      const maxVisible = 5
      let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
      let end = Math.min(totalPages.value, start + maxVisible - 1)

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1)
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    })

    const fetchOrders = async () => {
      loadingOrders.value = true
      try {
        const response = await apiStore.get('/admin/orders')
        orders.value = response.data || []
        totalOrders.value = orders.value.length
      } catch (error) {
        console.error('Failed to fetch orders:', error)
      } finally {
        loadingOrders.value = false
      }
    }

    const updateStatus = async (orderId, status) => {
      try {
        await apiStore.put(`/admin/orders/${orderId}/status`, { status })
        await fetchOrders()
      } catch (error) {
        console.error('Failed to update order status:', error)
      }
    }

    const getPaymentStatusClass = (status) => {
      switch (status) {
        case 'succeeded':
          return 'bg-success text-white'
        case 'pending':
        case 'processing':
          return 'bg-warning text-dark'
        case 'failed':
        case 'canceled':
          return 'bg-danger'
        case 'refunded':
          return 'bg-info text-dark'
        default:
          return 'bg-secondary'
      }
    }

    onMounted(() => {
      fetchOrders()
    })

    return {
      orders,
      loadingOrders,
      searchQuery,
      statusFilter,
      updateStatus,
      getPaymentStatusClass,
      currentPage,
      itemsPerPage,
      totalOrders,
      totalPages,
      paginatedOrders,
      visiblePages
    }
  }
}
</script>

<style scoped>
.admin-orders {
  padding: 20px 0;
}

.filters-container {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.search-box {
  position: relative;
}

.search-box i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
}

.search-box input {
  padding-left: 35px;
}

.admin-orders .btn-primary {
  background-color: var(--theme, #6B8F71);
  border-color: var(--theme, #6B8F71);
}

.admin-orders .btn-primary:hover {
  background-color: var(--header, #010F1C);
  border-color: var(--header, #010F1C);
}

/* Modern Table Styles */
.orders-table-container {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.8);
  overflow: hidden;
}

.modern-orders-table {
  width: 100%;
  border-collapse: collapse;
}

.modern-orders-table thead th {
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  color: #495057;
  font-weight: 600;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 20px 30px;
  text-align: left;
  border-bottom: 2px solid #dee2e6;
}

.modern-orders-table tbody tr {
  transition: all 0.3s ease;
  border-bottom: 1px solid #f1f3f4;
}

.modern-orders-table tbody tr:hover {
  background: linear-gradient(135deg, #f8f9fa, #f1f3f4);
}

.modern-orders-table tbody td {
  padding: 20px 30px;
  vertical-align: middle;
}

.order-id {
  font-weight: 600;
  color: var(--theme, #6B8F71);
}

.customer-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.customer-avatar {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--theme, #6B8F71), #5a7d5f);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
}

.customer-name {
  font-weight: 500;
  color: #2c3e50;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-block;
  text-align: center;
  min-width: 100px;
}

/* Payment Statuses - Muted Colors */
.status-badge.bg-success {
  background: #e8f5e9;
  color: #2e7d32;
}

.status-badge.bg-warning {
  background: #fff3e0;
  color: #ef6c00;
}

.status-badge.bg-danger {
  background: #ffebee;
  color: #c62828;
}

.status-badge.bg-info {
  background: #e1f5fe;
  color: #0277bd;
}

.status-badge.bg-secondary {
  background: #f5f5f5;
  color: #616161;
}

.order-total {
  font-weight: 600;
  color: #2c3e50;
}

.action-btn {
  background: rgba(107, 143, 113, 0.1);
  color: var(--theme, #6B8F71);
  padding: 8px 16px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
}

.action-btn:hover {
  background: var(--theme, #6B8F71);
  color: white;
  transform: translateY(-1px);
}

.status-select {
  border-radius: 8px;
  border: 1px solid #dee2e6;
  padding: 5px 10px;
  font-size: 13px;
  color: #495057;
}

/* Pagination Styles */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  margin-top: 20px;
  border-top: 1px solid #dee2e6;
}

.pagination-info {
  color: #6c757d;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  gap: 8px;
}

.pagination-btn {
  padding: 8px 16px;
  border: 1px solid #dee2e6;
  background: white;
  color: #495057;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.pagination-btn:hover:not(:disabled) {
  background: var(--theme, #6B8F71);
  color: white;
  border-color: var(--theme, #6B8F71);
}

.pagination-btn.active {
  background: var(--theme, #6B8F71);
  color: white;
  border-color: var(--theme, #6B8F71);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .pagination-container {
    flex-direction: column;
    gap: 15px;
  }
}
</style>